// src/features/auth/ResetPassword.tsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, TextField, Typography, Link as MuiLink, Alert, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const redirect = searchParams.get("redirect");

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("No reset token provided");
        setVerifying(false);
        return;
      }

      try {
        await api.get(`/users/password/verify-token?token=${token}`);
        setTokenValid(true);
      } catch (err: any) {
        setError(err.response?.data?.message || "Invalid or expired reset token");
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const onSubmit = async (data: FormData) => {
    try {
      setError(null);
      await api.post("/users/password/reset", {
        token,
        newPassword: data.newPassword,
      });
      setSuccess(true);
      const loginPath = redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : "/login";
      setTimeout(() => {
        navigate(loginPath);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to reset password");
    }
  };

  // Password validation helper
  const validatePassword = (value: string) => {
    if (value.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/[A-Z]/.test(value)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/[0-9]/.test(value)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return "Password must contain at least one special character";
    }
    return true;
  };

  if (verifying) {
    return (
      <Box maxWidth={400} mx="auto" mt={8} textAlign="center">
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Verifying reset token...</Typography>
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box maxWidth={500} mx="auto" mt={8}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || "Invalid or expired reset token"}
        </Alert>
        <Typography variant="body2" align="center">
          <MuiLink component={Link} to="/forgot-password">Request a new reset link</MuiLink>
        </Typography>
      </Box>
    );
  }

  if (success) {
    return (
      <Box maxWidth={500} mx="auto" mt={8}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Password reset successfully! Redirecting to login...
        </Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth={400} mx="auto" mt={8}>
      <Typography variant="h4" gutterBottom>
        Reset Password
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your new password below.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          label="New Password"
          type={showPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          {...register("newPassword", {
            required: "Password is required",
            validate: validatePassword,
          })}
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          {...register("confirmPassword", {
            required: "Please confirm your password",
            validate: (value) =>
              value === watch("newPassword") || "Passwords do not match",
          })}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }}>
          Reset Password
        </Button>
      </form>

      <Typography variant="body2" align="center" sx={{ mt: 2 }}>
        Remember your password? <MuiLink component={Link} to="/login">Log in</MuiLink>
      </Typography>
    </Box>
  );
};

export default ResetPassword;
